import { callApi } from '../utils';

// Backs the hover card behind every <UserLink>. A link only ever carries the
// slice of the user its own list embedded — fraud events give us `name`/`email`
// and nothing else — so the card seeds itself from whatever the caller has and
// fills the gaps from `GET /users/:id`, which returns the standard
// `{ data, meta }` envelope the rest of that resource family uses. (The older
// `GET /user?id=`, still used by the profile page and UserModal, is deprecated.)
//
// Hovering down a 30-row table would otherwise fire 30 requests, and re-hovering
// the same row would fire them again, so reads are memoised per user id and
// concurrent hovers on the same id share one in-flight promise. The TTL is short
// because a balance shown next to a customer's name is worth re-reading; it only
// exists to collapse the burst of hovers while an admin scans one screen.

const TTL = 60 * 1000;

const cache = new Map(); // id -> { at, summary }
const inFlight = new Map(); // id -> Promise<summary>

const num = (value) => {
  const n = Number(value);
  return isFinite(n) ? n : 0;
};

const str = (value) =>
  value === null || value === undefined || value === '' ? null : String(value);

// What to look the user up by. Orders, sessions and mcash records embed the
// customer's `phone` and no user id at all, and the `/users/:id` family resolves
// either — `/users/:phone/watchlists` is called with a phone today. The same
// reference addresses the profile page, so it doubles as the link href.
export const userReference = (user) => {
  const source = user || {};
  return str(source.id) || str(source.phone);
};

// Everything the card renders, in the units it renders them in. Unknown fields
// stay `null` rather than defaulting to 0 or '' — the card draws "—" for null,
// which is honest about a field the API never sent, whereas "₦0.00" for a user
// whose wallet simply wasn't embedded would be a lie.
export const normaliseUserSummary = (user) => {
  const source = user || {};
  const wallet = source.userWallet || null;

  return {
    id: source.id === undefined ? null : source.id,
    name: str(source.name),
    phone: str(source.phone),
    email: str(source.email),
    joinedAt: source.created_at || null,
    deletedAt: source.deleted_at || null,
    // `is_blocked` gates vending, not sign-in — see the Enable/Disable Vend
    // button on the profile page.
    vendBlocked: !!source.is_blocked,
    // Wallet columns are stored in kobo and every surface divides by 100 before
    // rendering; do it once, here.
    balance: wallet ? num(wallet.balance) / 100 : null,
    debt: wallet ? num(wallet.debt) / 100 : null,
  };
};

// `patch` is the authoritative read, so it wins wherever it has a value; `base`
// is the seed from the calling list and survives only in the gaps.
export const mergeUserSummary = (base, patch) => {
  const seed = base || {};
  const fresh = patch || {};
  const merged = { ...seed };

  Object.keys(fresh).forEach((key) => {
    if (fresh[key] !== null && fresh[key] !== undefined)
      merged[key] = fresh[key];
  });

  return merged;
};

export const fetchUserSummary = (id) => {
  const key = String(id);

  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < TTL) {
    return Promise.resolve(cached.summary);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  // The reference may be a phone number, so it has to survive the path.
  const request = callApi(`/users/${encodeURIComponent(key)}`)
    .then(({ data }) => {
      const summary = normaliseUserSummary(data);
      cache.set(key, { at: Date.now(), summary });
      return summary;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);

  return request;
};

// Test seam only — the cache is module state and would otherwise leak between
// specs.
export const __resetUserSummaryCache = () => {
  cache.clear();
  inFlight.clear();
};
